import React, { useContext, useEffect, useState } from 'react';
import { Route, Routes, useParams, Navigate } from 'react-router-dom';
import {
  LawChanges,
  RelatedLaws,
  LawOverview,
  LawInternalComments
} from './Law';
import { LawList } from './List/index';
import { Grid } from '@material-ui/core';
import { ConfirmationSessionProvider } from '../../../../Context/ComplianceSessionContext';
import { Container } from '../../../../Components';
import { ModuleContext } from '../../../../Context/ModuleContext/ModuleContext';
import { useAptorApi } from '../../../../Api';
import { useIntl } from 'react-intl';
import { AccessLevel, LawPortalFeature, UserContext } from '../../../../Context/UserContext/UserContext';
import { ChangesToAcknowledge } from "./Unacknowledged";
import { AddCustomerLaws } from './CustomerLaws/CustomerLaws.Add';
import { UpdateCustomerLaws } from './CustomerLaws/CustomerLaws.Update';
import { AcknowledgedChanges } from "./Acknowledged";

export interface ILawInfo {
  name: string;
  number: string;
  category: string;
}

const Law = ({ info, id }: { id: number; info: ILawInfo }) => {
  const { formatMessage } = useIntl();
  const { setHeader } = useContext(ModuleContext);
  const user = useContext(UserContext);
  const isManager = user.hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage);
  const isComplianceUser = user.complianceUserFor.laws.some((x) => x.id === id);

  useEffect(() => {
    const header = info ? `${info.category} ${info.number} ${info.name}` : '';
    const subMenu = [
      {
        title: formatMessage({ id: 'law-portal.our-laws.sub-menu.overview' }),
        to: `/law-portal/our-laws/${id}`,
      },
      {
        title: formatMessage({ id: 'law-portal.our-laws.sub-menu.changes' }),
        to: `/law-portal/our-laws/${id}/changes`,
      },
      {
        title: formatMessage({ id: 'law-portal.our-laws.sub-menu.related-laws' }),
        to: `/law-portal/our-laws/${id}/related-laws`,
      },
    ];
    setHeader(header, subMenu);
  }, [id, info, isComplianceUser, isManager, setHeader, formatMessage]);

  return (
    <Routes>
      <Route index element={<LawOverview />} />
      <Route path="changes" element={<LawChanges />} />
      <Route path="related-laws" element={<RelatedLaws />} />
    </Routes>
  );
};

const LawView = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = parseInt(idParam || '0');
  const { api, abortController } = useAptorApi();
  const [info, setInfo] = useState<ILawInfo>();

  useEffect(() => {
    api.getLawPortalLawInfo<ILawInfo>(id).then((result) => {
      if (abortController.current.signal.aborted) {
        return;
      }
      setInfo(result);
    });
  }, [api, abortController, id]);

  if (info === undefined) {
    return null;
  }

  return (
    <Grid container direction="row" spacing={2}>
      <Law info={info} id={id} />
      <LawInternalComments id={id} />
    </Grid>
  );
};

export const OurLaws = () => {
  const { formatMessage } = useIntl();
  const { setHeader } = useContext(ModuleContext);
  const { hasAccessToFeature } = useContext(UserContext);
  const canManage = hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage);
  const accessToOurLaws = hasAccessToFeature(LawPortalFeature.OurLaws, AccessLevel.Read);
  useEffect(() => {
    setHeader(formatMessage({ id: 'law-portal.our-laws.header', defaultMessage: 'Our laws' }), [
      {
        title: formatMessage({ id: 'law-portal.our-law.tab.law-list' }),
        to: `/law-portal/our-laws`,
      },
      {
        title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.unacknowledged' }),
        to: `/law-portal/our-laws/unacknowledged`,
      },
      {
        title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.acknowledged' }),
        to: `/law-portal/our-laws/acknowledged`,
      },
    ]);
  }, [setHeader, formatMessage]);

  return (
    <ConfirmationSessionProvider>
      <Container fullWidth>
        <Routes>
          <Route index element={<LawList />} />
          <Route path=":id/*" element={<LawView />} />
          <Route path="unacknowledged" element={<ChangesToAcknowledge />} />
          <Route path="acknowledged" element={<AcknowledgedChanges />} />
          {/* {canManage && accessToOurLaws && (
             <Route path="new" element={<AddCustomerLaws />} />
          )}
          {accessToOurLaws && (
         <Route
            path="update-for-company-unit/:unitid"
            element={<UpdateCustomerLaws />}
          />
          )} */}
        </Routes>
      </Container>
    </ConfirmationSessionProvider>
  );
};